import { withApplicationInsights } from "../../components/telemetry";
import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { bindActionCreators } from "../../actions/actionCreators";
import { AppContext } from "../../models/applicationState";
import { Context } from "../../components/context";
import * as listActions from "../../actions/listActions";
import * as userActions from "../../actions/userActions";
import * as itemActions from "../../actions/itemActions";
import * as eventActions from "../../actions/eventActions";
import { ListActions } from "../../actions/listActions";
import { UserActions } from "../../actions/userActions";
import { ItemActions } from "../../actions/itemActions";
import { EventActions } from "../../actions/eventActions";
import { CalendarEvent } from "../../models/calendarEvent";
import { CalendarConnection } from "../../models/user";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isTomorrow from "dayjs/plugin/isTomorrow";
import {
  CircularProgress,
  Box,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  Meeting,
  MeetingItemStatus,
  MeetingStatus,
  RiskIndicator,
} from "../../models";

import ConnectCalendarDialog from "../../components/connectCalendarDialog";
import CreateMeetingDialog from "../../components/createMeetingDialog";
import MonthlyCalendarView from "../../components/calendar/MonthlyCalendarView";
import MeetingDetailsDialog from "../../components/calendar/MeetingDetailsDialog";
import {
  mapGoogleEvent as mapGoogleEventBase,
  mapOutlookEvent as mapOutlookEventBase,
} from "../../util/calendarEvent";
import { useCalendarEvents } from "../../hooks/useCalendarEvents";

dayjs.extend(isToday);
dayjs.extend(isTomorrow);

const SEARCH_PAST_DAYS = 365 * 5;
const SEARCH_FUTURE_DAYS = 365 * 5;

const formatEventTime = (value?: Date | string): string => {
  if (!value) return "";
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat(i18next.language || undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

function matchesSearch(event: CalendarEvent, lowerSearchText: string): boolean {
  return (
    !!event.name?.toLowerCase().includes(lowerSearchText) ||
    !!event.creator?.toLowerCase().includes(lowerSearchText) ||
    !!event.invitees?.some((invitee) =>
      invitee.toLowerCase().includes(lowerSearchText),
    )
  );
}

const Events = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const appContext = useContext<AppContext>(Context);
  const invokerSettled = appContext.state.invoker !== undefined;
  const actions = useMemo(
    () => ({
      lists: bindActionCreators(
        listActions,
        appContext.dispatch,
      ) as unknown as ListActions,
      users: bindActionCreators(
        userActions,
        appContext.dispatch,
      ) as unknown as UserActions,
      items: bindActionCreators(
        itemActions,
        appContext.dispatch,
      ) as unknown as ItemActions,
      events: bindActionCreators(
        eventActions,
        appContext.dispatch,
      ) as unknown as EventActions,
    }),
    [appContext.dispatch],
  );

  const [createMeetingOpen, setCreateMeetingOpen] = useState(false);
  const [includePastInSearch, setIncludePastInSearch] = useState(false);

  const rawConnections = appContext.state.invoker?.calendarConnections ?? [];
  const connectionsKey = JSON.stringify(
    rawConnections.map((c) => `${c.provider}:${c.accountUsername}`),
  );
  const connections: CalendarConnection[] = useMemo(
    () => rawConnections,
    [connectionsKey], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const hasConnections = connections.length > 0;

  // Meetings page keys events by raw provider id to match `eventId` stored on Meeting records.
  const mapOutlookEventForPage = useCallback(
    (raw: unknown, accountUsername?: string): CalendarEvent =>
      mapOutlookEventBase(raw, accountUsername, {
        includeLocation: true,
        includeOnlineMeeting: true,
      }),
    [],
  );
  const mapGoogleEventForPage = useCallback(
    (raw: unknown, accountUsername?: string): CalendarEvent =>
      mapGoogleEventBase(raw, accountUsername, { includeLocation: true }),
    [],
  );
  const matchEvent = useCallback(
    (event: CalendarEvent, lower: string) => matchesSearch(event, lower),
    [],
  );
  const refreshInvoker = useCallback(
    () => actions.users.getInvoker(),
    [actions.users],
  );

  const calendar = useCalendarEvents<CalendarEvent>({
    connections,
    refreshInvoker,
    mapOutlookEvent: mapOutlookEventForPage,
    mapGoogleEvent: mapGoogleEventForPage,
    matchEvent,
    t,
    searchPastDays: includePastInSearch ? SEARCH_PAST_DAYS : 0,
    searchFutureDays: SEARCH_FUTURE_DAYS,
    restoreOriginalAccountOnRedirect: true,
    showConnectionSnackbars: true,
  });

  const {
    filteredEvents,
    connectionStatuses,
    searchText,
    setSearchText,
    isSearchActive,
    searchInProgress,
    loading,
    viewedMonth,
    setViewedMonth,
    handlePrevMonth,
    handleNextMonth,
    availableMonths,
    connectLoading,
    connectError,
    setConnectError,
    handleConnectCalendar,
    handleDisconnectAccount,
    fetchAllEvents,
    snackbar,
    setSnackbar,
  } = calendar;

  // Detail event for the MeetingDetailsDialog (opened on row double-click)
  const [detailEvent, setDetailEvent] = useState<CalendarEvent | null>(null);

  // ── Meetings bucket ────────────────────────────────────────────────
  const bucketMeetingsByEventId = (meetings: Meeting[]) => {
    return (meetings || []).reduce(
      (acc, meeting) => {
        if (meeting.eventId) acc[meeting.eventId] = meeting;
        return acc;
      },
      {} as { [eventId: string]: Meeting },
    );
  };

  const [meetings, setMeetings] = useState(
    bucketMeetingsByEventId(appContext.state.lists),
  );

  useEffect(() => {
    setMeetings(bucketMeetingsByEventId(appContext.state.lists));
  }, [appContext.state.lists, navigate]);

  // ── Navigation helpers ─────────────────────────────────────────────
  const onNavigate = (meeting: Meeting, event?: CalendarEvent) => {
    if (meeting) {
      if (event) {
        actions.events.select(event);
      }
      actions.lists.select(meeting);

      if (meeting.status === MeetingStatus.Published) {
        navigate(`/library/${meeting.id}/published`, {
          state: { blockPrevious: true },
        });
        return;
      }

      navigate(`/meetings/workflow/${meeting.id}`, {
        state: { blockPrevious: true },
      });
    }
  };

  const mergeMeetingWithEvent = (
    meeting: Meeting,
    event: CalendarEvent,
  ): Meeting => {
    const startTime = formatEventTime(event.startDate);
    const endTime = formatEventTime(event.endDate);

    return {
      ...meeting,
      date: meeting.date || event.startDate,
      startTime: meeting.startTime || startTime || undefined,
      endTime: meeting.endTime || endTime || undefined,
      participants:
        meeting.participants && meeting.participants.length > 0
          ? meeting.participants
          : event.invitees,
      eventId: meeting.eventId || event.id,
      calendarOrganizerEmail: meeting.calendarOrganizerEmail || event.creator,
      teamsJoinWebUrl: meeting.teamsJoinWebUrl || event.onlineMeetingUrl,
    };
  };

  // Guard against rapid double-clicks racing two POSTs for the same event.
  const inFlightEventIds = useRef<Set<string>>(new Set());

  const onEventSelected = async (event: CalendarEvent) => {
    const existingMeeting = meetings[event.id!];
    if (existingMeeting) {
      const syncedMeeting = mergeMeetingWithEvent(existingMeeting, event);
      const needsSync =
        (!existingMeeting.date && !!event.startDate) ||
        (!existingMeeting.startTime && !!syncedMeeting.startTime) ||
        (!existingMeeting.endTime && !!syncedMeeting.endTime) ||
        (!existingMeeting.eventId && !!event.id) ||
        ((!existingMeeting.participants ||
          existingMeeting.participants.length === 0) &&
          !!event.invitees?.length) ||
        (!existingMeeting.calendarOrganizerEmail && !!event.creator) ||
        (!existingMeeting.teamsJoinWebUrl && !!event.onlineMeetingUrl);

      const meetingToOpen = needsSync
        ? await actions.lists.save(syncedMeeting)
        : existingMeeting;
      onNavigate(meetingToOpen, event);
      return;
    }
    if (event.id && inFlightEventIds.current.has(event.id)) {
      return;
    }
    if (event.id) inFlightEventIds.current.add(event.id);
    try {
      const saved = await actions.lists.save({
        name: event.name,
        date: event.startDate,
        startTime: formatEventTime(event.startDate) || undefined,
        endTime: formatEventTime(event.endDate) || undefined,
        participants: event.invitees,
        eventId: event.id,
        calendarOrganizerEmail: event.creator,
        teamsJoinWebUrl: event.onlineMeetingUrl,
      });
      onNavigate(saved, event);
    } finally {
      if (event.id) inFlightEventIds.current.delete(event.id);
    }
  };

  // ── Create meeting (kept for existing wiring) ─────────────────────
  const handleCreateMeeting = async (meeting: {
    name: string;
    date: string;
    time: string;
    duration: string;
    invitees: string;
  }): Promise<{ meetingId: string; itemId: string } | undefined> => {
    const dateTime = dayjs(`${meeting.date} ${meeting.time}`).toDate();
    const participants = meeting.invitees
      ? meeting.invitees
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    const saved = await actions.lists.save({
      name: meeting.name,
      date: dateTime,
      participants,
    });
    if (!saved?.id) return undefined;
    const item = await actions.items.save(saved.id, {
      content: meeting.name,
      status: MeetingItemStatus.NotStarted,
      risk: RiskIndicator.NoRisk,
    });
    return { meetingId: saved.id, itemId: item.id! };
  };

  // ── Empty state ────────────────────────────────────────────────────
  const renderNoCalendarConnected = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "calc(100vh - 64px)",
        gap: 3,
      }}
    >
      <CalendarTodayIcon sx={{ fontSize: 64, color: "text.disabled" }} />
      <Typography variant="h5" sx={{ fontWeight: 600, color: "text.primary" }}>
        {t("calendar.noCalendarTitle")}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: "text.secondary", textAlign: "center", maxWidth: 400 }}
      >
        {t("calendar.noCalendarDescription")}
      </Typography>
      <ConnectCalendarDialog
        onConnect={handleConnectCalendar}
        loading={connectLoading}
        error={connectError}
        onOpenDialog={() => setConnectError(null)}
        googleEnabled={true}
        outlookEnabled={true}
      />
    </Box>
  );

  const connectionsSlot = (
    <ConnectCalendarDialog
      onConnect={handleConnectCalendar}
      loading={connectLoading}
      error={connectError}
      connections={connections}
      onDisconnect={handleDisconnectAccount}
      onOpenDialog={() => setConnectError(null)}
      connectionStatuses={connectionStatuses}
      googleEnabled={true}
      outlookEnabled={true}
    />
  );

  const pageLoading = !invokerSettled;
  const pageContent = pageLoading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "calc(100vh - 64px)",
      }}
    >
      <CircularProgress size={30} />
    </Box>
  ) : hasConnections ? (
    <>
      <MonthlyCalendarView
        events={filteredEvents}
        loading={loading}
        viewedMonth={viewedMonth}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onSetMonth={setViewedMonth}
        availableMonths={availableMonths}
        searchText={searchText}
        onSearchChange={setSearchText}
        isSearchActive={isSearchActive}
        searchInProgress={searchInProgress}
        connectionsSlot={connectionsSlot}
        onRefresh={fetchAllEvents}
        onEventClick={onEventSelected}
        onRowDoubleClick={(event) => setDetailEvent(event)}
        enableConflictDetection={true}
        connectionStatuses={connectionStatuses}
        title={t("calendar.yourCalendar", { defaultValue: "Your Calendar" })}
        includePastInSearch={includePastInSearch}
        onTogglePastInSearch={setIncludePastInSearch}
      />
      <CreateMeetingDialog
        open={createMeetingOpen}
        onClose={() => setCreateMeetingOpen(false)}
        onSave={handleCreateMeeting}
      />
    </>
  ) : (
    renderNoCalendarConnected()
  );

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        minHeight: "calc(100vh - 64px)",
        position: "relative",
      }}
    >
      {pageContent}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "error" ? 8000 : 4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ position: "absolute" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <MeetingDetailsDialog
        open={detailEvent !== null}
        event={detailEvent}
        onClose={() => setDetailEvent(null)}
      />
    </Box>
  );
};

export default withApplicationInsights(Events, "Events");
